import React from 'react';
import { useQuery } from '@apollo/client';
import { adminBrandQuery } from '../../graphql/queries';
import { EditItem } from '../../components/Layout';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Table } from 'reactstrap';
import { ContentSection } from '../../components/Layout/ContentSection';
import { LoadingContainer } from '../../components/Layout';

type BrandChangeHistoryProps = {
  brandTextId: string;
  title: string;
};

type ChangeHistoryEntry = {
  old_values?: string | null;
  new_values?: string | null;
  changed_at?: string | null;
  changed_by?: string | null;
  changed_by_raw?: string | null;
};

export function BrandChangeHistory(props: BrandChangeHistoryProps) {
  const { brandTextId } = props;
  const { data = { brand: null }, loading } = useQuery(adminBrandQuery, {
    fetchPolicy: 'no-cache',
    skip: !brandTextId,
    variables: { id: brandTextId },
  });
  const brand = data.brand;
  const changeHistory: ChangeHistoryEntry[] =
    (brand?.change_history as ChangeHistoryEntry[]) || [];

  if (loading) {
    return <LoadingContainer />;
  }

  function getUserLink(userId: string | undefined | null) {
    if (userId == null) {
      return 'unknown';
    }
    const uuidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    if (
      uuidRegex.test(userId) &&
      userId !== '00000000-0000-0000-0000-000000000000'
    ) {
      return (
        <a href={`/users/${userId}`} target="_blank" rel="noopener noreferrer">
          {userId}
        </a>
      );
    }
    const salesforceIdRegex = /^sfu_005[a-zA-Z0-9]{15}$/;
    if (salesforceIdRegex.test(userId)) {
      return (
        <a
          href={`https://vytalglobalgmbh.lightning.force.com/lightning/r/User/${userId.replace(
            'sfu_',
            '',
          )}/view`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {userId}
        </a>
      );
    }
    return userId;
  }

  const rows = changeHistory
    .sort(
      (a, b) => (b?.changed_at || '').localeCompare(a?.changed_at || '') || 0,
    )
    .map((row) => {
      const oldValues = JSON.parse(row?.old_values || '{}');
      const newValues = JSON.parse(row?.new_values || '{}');
      const keys = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ]);
      return Array.from(keys).map((key) => {
        const oldValue =
          typeof oldValues[key] === 'string'
            ? oldValues[key]
            : JSON.stringify(oldValues[key]);
        const newValue =
          typeof newValues[key] === 'string'
            ? newValues[key]
            : JSON.stringify(newValues[key]);
        return (
          <tr key={key + row?.changed_at}>
            <td>{key}</td>
            <td>{oldValue}</td>
            <td>{newValue}</td>
            <td>
              {new Date(row?.changed_at || 0).toLocaleDateString(
                i18n.language,
                getDateFormatOptions(true),
              )}
            </td>
            <td>{getUserLink(row?.changed_by || row?.changed_by_raw)}</td>
          </tr>
        );
      });
    })
    .flat();

  return (
    <ContentSection title={props.title} collapsable={false} key="content">
      <EditItem title={i18n.t('Tabs:changeHistory')}>
        <Table striped hover>
          <thead>
            <tr>
              <th>{i18n.t('ChangeHistory:attribute')}</th>
              <th>{i18n.t('ChangeHistory:old')}</th>
              <th>{i18n.t('ChangeHistory:new')}</th>
              <th>{i18n.t('ChangeHistory:changedAt')}</th>
              <th>{i18n.t('ChangeHistory:changedBy')}</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      </EditItem>
    </ContentSection>
  );
}
